import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, Eye, EyeOff, Loader2, AlertCircle, CheckCircle, KeyRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  React.useEffect(() => {
    if (location.pathname.includes('register')) {
      setActiveTab('register');
    } else if (location.pathname.includes('login')) {
      setActiveTab('login');
    }
  }, [location.pathname]);

  // Form states
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  const [regUsername, setRegUsername] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');

  const [resetEmail, setResetEmail] = useState('');

  // Verification code states
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingUserId, setPendingUserId] = useState('');

  const { login, forgotPassword } = useAuth();
  const navigate = useNavigate();

  const from = location.state?.from?.pathname || '/app';

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(loginEmail, loginPassword);
      toast.success(t('auth.loginSuccess'));
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (regPassword !== regConfirmPassword) {
      setError(t('auth.passwordsMismatch'));
      return;
    }

    if (regPassword.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: regUsername,
          email: regEmail,
          password: regPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('auth.registerError'));
      }

      // Show verification code input
      setPendingUserId(data.user_id);
      setShowVerification(true);
      setSuccess(t('auth.checkEmail'));
      toast.success(t('auth.codeSentToast'));
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: pendingUserId,
          code: verificationCode
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('auth.codeInvalid'));
      }

      setSuccess(t('auth.emailVerified'));
      toast.success(t('auth.accountActivated'));
      setShowVerification(false);
      setActiveTab('login');
      // Pre-fill email for login
      setLoginEmail(regEmail);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: pendingUserId })
      });

      if (response.ok) {
        toast.success(t('auth.newCodeSent'));
      }
    } catch (err) {
      toast.error(t('auth.resendError'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await forgotPassword(resetEmail);
      setSuccess(t('auth.resetSuccess'));
      toast.success(t('auth.checkEmailToast'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'login', name: t('auth.tabLogin') },
    { id: 'register', name: t('auth.tabRegister') },
    { id: 'reset', name: t('auth.tabReset') },
  ];

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-4" data-testid="auth-page">
      <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="glass rounded-lg overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-white/10 text-center">
            <h1 className="font-rajdhani text-3xl font-bold uppercase tracking-wider">
              <span className="text-white">World</span>
              <span className="text-neon-cyan neon-text"> Gaming</span>
            </h1>
            <p className="text-muted-foreground text-sm mt-2">{t('auth.subtitle')}</p>
          </div>

          {/* Tabs */}
          {!showVerification && (
            <div className="flex border-b border-white/10">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setError(''); setSuccess(''); }}
                  className={`flex-1 py-3 font-rajdhani font-semibold text-sm uppercase tracking-wider transition-colors ${
                    activeTab === tab.id
                      ? 'text-neon-cyan border-b-2 border-neon-cyan bg-neon-cyan/5'
                      : 'text-muted-foreground hover:text-white'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {/* Error/Success Messages */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded flex items-center gap-2 text-red-400 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}
              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded flex items-center gap-2 text-green-400 text-sm"
                >
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  {success}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Verification Code Form */}
            {showVerification ? (
              <motion.form
                key="verify"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleVerifyCode}
                className="space-y-4"
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-neon-cyan/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <KeyRound className="w-8 h-8 text-neon-cyan" />
                  </div>
                  <h2 className="font-rajdhani text-xl font-bold text-white">{t('auth.verifyEmail')}</h2>
                  <p className="text-muted-foreground text-sm mt-2">
                    {t('auth.codeSent')}<br />
                    <span className="text-neon-cyan">{regEmail}</span>
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">{t('auth.verificationCode')}</label>
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-[0.5em] bg-white/5 border-white/10 focus:border-neon-cyan"
                    maxLength={6}
                    required
                    data-testid="verification-code"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full bg-neon-cyan text-black font-rajdhani font-bold uppercase tracking-wider hover:shadow-[0_0_20px_rgba(0,243,255,0.5)]"
                  data-testid="verify-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.verifyBtn')}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={loading}
                    className="text-sm text-muted-foreground hover:text-neon-cyan transition-colors"
                    data-testid="resend-code"
                  >
                    {t('auth.noCode')} <span className="underline">{t('auth.resend')}</span>
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setShowVerification(false)}
                  className="w-full text-sm text-muted-foreground hover:text-white transition-colors"
                  data-testid="back-to-register"
                >
                  ← {t('auth.backToRegister')}
                </button>
              </motion.form>
            ) : (
              <>
                {/* Login Form */}
                <AnimatePresence mode="wait">
                  {activeTab === 'login' && (
                    <motion.form
                      key="login"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      onSubmit={handleLogin}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <label className="text-sm text-muted-foreground">{t('auth.emailOrUsername')}</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="text"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            placeholder={t('auth.emailPlaceholder')}
                            className="pl-10 bg-white/5 border-white/10 focus:border-neon-cyan"
                            required
                            data-testid="login-email"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm text-muted-foreground">{t('auth.password')}</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            placeholder="••••••••"
                            className="pl-10 pr-10 bg-white/5 border-white/10 focus:border-neon-cyan"
                            required
                            data-testid="login-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                            data-testid="login-toggle-password"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-neon-cyan text-black font-rajdhani font-bold uppercase tracking-wider hover:shadow-[0_0_20px_rgba(0,243,255,0.5)]"
                        data-testid="login-submit"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.loginBtn')}
                      </Button>

                    </motion.form>
                  )}

                  {/* Register Form */}
                  {activeTab === 'register' && (
                    <motion.form
                      key="register"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      onSubmit={handleRegister}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <label className="text-sm text-muted-foreground">{t('auth.username')}</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="text"
                            value={regUsername}
                            onChange={(e) => setRegUsername(e.target.value)}
                            placeholder={t('auth.usernamePlaceholder')}
                            className="pl-10 bg-white/5 border-white/10 focus:border-neon-cyan"
                            required
                            data-testid="register-username"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm text-muted-foreground">{t('auth.email')}</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="email"
                            value={regEmail}
                            onChange={(e) => setRegEmail(e.target.value)}
                            placeholder={t('auth.emailPlaceholder')}
                            className="pl-10 bg-white/5 border-white/10 focus:border-neon-cyan"
                            required
                            data-testid="register-email"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm text-muted-foreground">{t('auth.password')}</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={regPassword}
                            onChange={(e) => setRegPassword(e.target.value)}
                            placeholder={t('auth.minChars')}
                            className="pl-10 pr-10 bg-white/5 border-white/10 focus:border-neon-cyan"
                            required
                            data-testid="register-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                            data-testid="register-toggle-password"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm text-muted-foreground">{t('auth.confirmPassword')}</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={regConfirmPassword}
                            onChange={(e) => setRegConfirmPassword(e.target.value)}
                            placeholder={t('auth.repeatPassword')}
                            className="pl-10 bg-white/5 border-white/10 focus:border-neon-cyan"
                            required
                            data-testid="register-confirm-password"
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-neon-purple text-white font-rajdhani font-bold uppercase tracking-wider hover:shadow-[0_0_20px_rgba(188,19,254,0.5)]"
                        data-testid="register-submit"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.createAccount')}
                      </Button>
                    </motion.form>
                  )}

                  {/* Reset Password Form */}
                  {activeTab === 'reset' && (
                    <motion.form
                      key="reset"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      onSubmit={handleForgotPassword}
                      className="space-y-4"
                    >
                      <p className="text-muted-foreground text-sm mb-4">
                        {t('auth.resetDesc')}
                      </p>

                      <div className="space-y-2">
                        <label className="text-sm text-muted-foreground">{t('auth.email')}</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            placeholder={t('auth.emailPlaceholder')}
                            className="pl-10 bg-white/5 border-white/10 focus:border-neon-cyan"
                            required
                            data-testid="reset-email"
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-neon-cyan text-black font-rajdhani font-bold uppercase tracking-wider hover:shadow-[0_0_20px_rgba(0,243,255,0.5)]"
                        data-testid="reset-submit"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.sendLink')}
                      </Button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;